import { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  Tabs,
  Tab,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  ExpandMore,
  CheckCircle,
  Error as ErrorIcon,
  Description,
  Timer,
} from '@mui/icons-material';
import { EditorView } from '@components/BlockNote/EditorView';
import { AppHeader } from '../components/app-header';
import validatedOutputData from './fixtures/validated_output.json';

interface ValidatedOutput {
  metadata: {
    generated_at: string;
    script: string;
    template_id: string;
    legacy_template_id: string;
    note_ids: string[];
    target_model: string;
    total_sections_tested: number;
    total_runs: number;
    successful_runs: number;
    failed_runs: number;
  };
  summary: {
    total_elapsed_seconds: number;
    sections_successful: number;
    sections_failed: number;
  };
  sections: Array<{
    section_id: string;
    section_title: string;
    success: boolean;
    total_elapsed_seconds: number;
    error: string | null;
    examples: Array<{
      example_index: number;
      note_id: string;
      original_approved: string;
      generations: Array<{
        run_index: number;
        new_generation: string;
        model_used: string | null;
        elapsed_seconds: number;
        error: string | null;
      }>;
    }>;
  }>;
}

export default function TemplateConversionOutputViewer() {
  const [data, setData] = useState<ValidatedOutput | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedTab, setSelectedTab] = useState<{ [key: string]: number }>({});

  useEffect(() => {
    try {
      setData(validatedOutputData as ValidatedOutput);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load data');
    } finally {
      setLoading(false);
    }
  }, []);

  const handleTabChange = (sectionId: string, newValue: number) => {
    setSelectedTab(prev => ({ ...prev, [sectionId]: newValue }));
  };

  const formatDuration = (seconds: number) => {
    if (seconds < 1) {
      return `${(seconds * 1000).toFixed(0)}ms`;
    }
    return `${seconds.toFixed(2)}s`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <AppHeader />
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
            <CircularProgress />
          </Box>
        </main>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen bg-gray-50">
        <AppHeader />
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Alert severity="error">
            {error || 'Failed to load validation output'}
          </Alert>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <AppHeader />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">
            Template Conversion Output Viewer
          </h1>

          {/* Metadata Card */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Metadata
              </Typography>
              <Box display="flex" flexWrap="wrap" gap={1} mb={2}>
                <Chip
                  label={`Template: ${data.metadata.template_id.substring(0, 8)}...`}
                  size="small"
                  variant="outlined"
                />
                <Chip
                  label={`Legacy: ${data.metadata.legacy_template_id.substring(0, 8)}...`}
                  size="small"
                  variant="outlined"
                />
                <Chip
                  label={`Model: ${data.metadata.target_model}`}
                  size="small"
                  variant="outlined"
                  color="primary"
                />
                <Chip
                  icon={<Timer />}
                  label={`Total Time: ${formatDuration(data.summary.total_elapsed_seconds)}`}
                  size="small"
                  variant="outlined"
                />
              </Box>
              <Box display="flex" gap={2} flexWrap="wrap">
                <Typography variant="body2" color="text.secondary">
                  <strong>Sections Tested:</strong> {data.metadata.total_sections_tested}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  <strong>Total Runs:</strong> {data.metadata.total_runs}
                </Typography>
                <Typography variant="body2" color="success.main">
                  <strong>Successful:</strong> {data.metadata.successful_runs}
                </Typography>
                {data.metadata.failed_runs > 0 && (
                  <Typography variant="body2" color="error.main">
                    <strong>Failed:</strong> {data.metadata.failed_runs}
                  </Typography>
                )}
              </Box>
            </CardContent>
          </Card>

          {/* Summary Card */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Summary
              </Typography>
              <Box display="flex" gap={3} flexWrap="wrap">
                <Box display="flex" alignItems="center" gap={1}>
                  <CheckCircle color="success" />
                  <Typography variant="body1">
                    <strong>{data.summary.sections_successful}</strong> sections successful
                  </Typography>
                </Box>
                {data.summary.sections_failed > 0 && (
                  <Box display="flex" alignItems="center" gap={1}>
                    <ErrorIcon color="error" />
                    <Typography variant="body1" color="error.main">
                      <strong>{data.summary.sections_failed}</strong> sections failed
                    </Typography>
                  </Box>
                )}
              </Box>
            </CardContent>
          </Card>

          {/* Sections */}
          {data.sections.map((section, sectionIndex) => (
            <Card key={section.section_id} sx={{ mb: 3 }}>
              <CardContent>
                <Box display="flex" alignItems="center" gap={2} mb={2}>
                  <Typography variant="h6">{section.section_title}</Typography>
                  {section.success ? (
                    <Chip
                      icon={<CheckCircle />}
                      label="Success"
                      color="success"
                      size="small"
                    />
                  ) : (
                    <Chip
                      icon={<ErrorIcon />}
                      label="Failed"
                      color="error"
                      size="small"
                    />
                  )}
                  <Chip
                    label={`${formatDuration(section.total_elapsed_seconds)}`}
                    size="small"
                    variant="outlined"
                  />
                </Box>

                {section.error && (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    {section.error}
                  </Alert>
                )}

                {/* Examples */}
                {section.examples.map((example, exampleIndex) => {
                  const sectionKey = `${section.section_id}-${example.example_index}`;
                  const currentTab = selectedTab[sectionKey] ?? 0;

                  return (
                    <Accordion key={example.example_index} defaultExpanded={exampleIndex === 0}>
                      <AccordionSummary expandIcon={<ExpandMore />}>
                        <Box display="flex" alignItems="center" gap={2} width="100%">
                          <Typography variant="subtitle1">
                            Example {example.example_index + 1}
                          </Typography>
                          <Chip
                            label={`Note: ${example.note_id.substring(0, 8)}...`}
                            size="small"
                            variant="outlined"
                            sx={{ fontFamily: 'monospace', fontSize: '0.7rem' }}
                          />
                          <Chip
                            label={`${example.generations.length} generation${example.generations.length !== 1 ? 's' : ''}`}
                            size="small"
                            variant="outlined"
                          />
                        </Box>
                      </AccordionSummary>
                      <AccordionDetails>
                        <Box>
                          {/* Tabs above right column */}
                          {example.generations.length > 0 && (
                            <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end' }}>
                              <Tabs
                                value={currentTab}
                                onChange={(_, newValue) => handleTabChange(sectionKey, newValue)}
                                sx={{ borderBottom: 1, borderColor: 'divider' }}
                              >
                                {example.generations.map((gen, genIndex) => (
                                  <Tab
                                    key={genIndex}
                                    label={
                                      <Box display="flex" alignItems="center" gap={1}>
                                        <span>Run {gen.run_index + 1}</span>
                                        {gen.error && (
                                          <ErrorIcon color="error" sx={{ fontSize: '1rem' }} />
                                        )}
                                        {!gen.error && (
                                          <Chip
                                            label={formatDuration(gen.elapsed_seconds)}
                                            size="small"
                                            sx={{ height: '18px', fontSize: '0.65rem' }}
                                          />
                                        )}
                                      </Box>
                                    }
                                  />
                                ))}
                              </Tabs>
                            </Box>
                          )}

                          {/* Side by side layout */}
                          <Box
                            sx={{
                              display: 'flex',
                              gap: 2,
                              alignItems: 'stretch',
                            }}
                          >
                            {/* Left side - Original Approved (always visible) */}
                            <Box
                              sx={{
                                flex: 1,
                                display: 'flex',
                                flexDirection: 'column',
                                border: 1,
                                borderColor: 'divider',
                                borderRadius: 1,
                                p: 2,
                                bgcolor: 'grey.50',
                                minHeight: 0,
                              }}
                            >
                              <Typography
                                variant="caption"
                                fontWeight="bold"
                                color="text.secondary"
                                gutterBottom
                                display="block"
                                sx={{ mb: 1, height: '20px', display: 'flex', alignItems: 'center' }}
                              >
                                Original Approved Text
                              </Typography>
                              <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
                                <EditorView
                                  markdown={example.original_approved}
                                  editable={false}
                                  sideMenu={false}
                                  formattingToolbar={false}
                                />
                              </Box>
                            </Box>

                            {/* Right side - Generations */}
                            <Box
                              sx={{
                                flex: 1,
                                display: 'flex',
                                flexDirection: 'column',
                                minHeight: 0,
                              }}
                            >
                              {example.generations.length > 0 ? (
                                (() => {
                                  const gen = example.generations[currentTab];
                                  if (!gen) {
                                    return null;
                                  }
                                  if (gen.error) {
                                    return (
                                      <Alert severity="error">
                                        <Typography variant="body2">
                                          <strong>Error:</strong> {gen.error}
                                        </Typography>
                                      </Alert>
                                    );
                                  }
                                  return (
                                    <Box
                                      sx={{
                                        flex: 1,
                                        display: 'flex',
                                        flexDirection: 'column',
                                        border: 1,
                                        borderColor: 'divider',
                                        borderRadius: 1,
                                        p: 2,
                                        bgcolor: 'grey.50',
                                        minHeight: 0,
                                      }}
                                    >
                                      <Box 
                                        display="flex" 
                                        justifyContent="space-between" 
                                        alignItems="center" 
                                        mb={1}
                                        sx={{ height: '20px', display: 'flex', alignItems: 'center' }}
                                      >
                                        <Typography
                                          variant="caption"
                                          fontWeight="bold"
                                          color="text.secondary"
                                        >
                                          Generated Text (Run {gen.run_index + 1})
                                        </Typography>
                                        <Box display="flex" gap={1} alignItems="center">
                                          {gen.model_used && (
                                            <Chip
                                              label={gen.model_used}
                                              size="small"
                                              variant="outlined"
                                            />
                                          )}
                                          <Chip
                                            label={formatDuration(gen.elapsed_seconds)}
                                            size="small"
                                            variant="outlined"
                                          />
                                        </Box>
                                      </Box>
                                      <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
                                        <EditorView
                                          markdown={gen.new_generation}
                                          editable={false}
                                          sideMenu={false}
                                          formattingToolbar={false}
                                        />
                                      </Box>
                                    </Box>
                                  );
                                })()
                              ) : (
                                <Alert severity="info">
                                  No generations available for this example
                                </Alert>
                              )}
                            </Box>
                          </Box>
                        </Box>
                      </AccordionDetails>
                    </Accordion>
                  );
                })}
              </CardContent>
            </Card>
          ))}
        </div>
      </main>
    </div>
  );
}
